import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Briefcase,
  Trophy,
  CheckCircle,
  Clock,
  Lightning,
  Info,
  Warning,
  CaretDown,
  Sparkle,
} from "@phosphor-icons/react";

/**
 * Performance commerciale — V4.15.
 *
 * Doctrine : "machine à produire de la confiance". Chaque chiffre doit pouvoir
 * être utilisé dans : RDV agent immobilier, présentation réseau, support
 * commercial, argument de recrutement.
 *
 * 4 KPIs + carte de synthèse pré-formatée :
 *  1. Taux transformation compromis → acte (KPI HERO)
 *  2. Respect des délais offre de prêt
 *  3. Avance moyenne sur l'offre
 *  4. Délai compromis → offre vs marché
 *
 * Transparence : chaque KPI expose son échantillon + définition dépliable.
 * Safe = Silence : KPI sans données → masqué.
 */

const MIN_SAMPLE = 5;

function toneColors(tone) {
  return {
    good: { fg: "var(--cf-success-text)", bg: "var(--cf-success-soft)", accent: "var(--cf-success)" },
    warning: { fg: "var(--cf-warning-text)", bg: "var(--cf-warning-soft)", accent: "var(--cf-warning)" },
    bad: { fg: "var(--cf-danger-text)", bg: "var(--cf-danger-soft)", accent: "var(--cf-danger)" },
    neutral: { fg: "var(--cf-primary-hover)", bg: "var(--cf-primary-soft)", accent: "var(--cf-primary)" },
    muted: { fg: "var(--cf-neutral-text)", bg: "var(--cf-bg-2)", accent: "var(--cf-text-3)" },
  }[tone] || { fg: "var(--cf-neutral-text)", bg: "var(--cf-bg-2)", accent: "var(--cf-text-3)" };
}

function KpiInfoToggle({ expanded, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0"
      aria-label="Définition du KPI"
      data-testid={testid}
    >
      <Info size={13} weight={expanded ? "fill" : "regular"} />
    </button>
  );
}

function SampleBadge({ size }) {
  if (size == null) return null;
  const thin = size < MIN_SAMPLE;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded ${
        thin ? "text-[color:var(--cf-warning-text)] bg-[color:var(--cf-warning-soft)]" : "text-slate-500 bg-slate-100"
      }`}
      title={thin ? "Échantillon < 5 — usage interne uniquement" : ""}
    >
      {thin && <Warning size={9} weight="fill" />}
      {size} dossier{size > 1 ? "s" : ""}
    </span>
  );
}

// ============================================================================
// KPI 1 — HERO : Taux compromis → acte
// ============================================================================
function KpiTransformationHero({ data, periodLabel, expanded, onToggle }) {
  const rate = data.rate_pct;
  const colors = toneColors(data.tone);
  const hasData = rate !== null;
  const delta = data.delta_points;

  return (
    <div
      className="rounded-2xl p-5 mb-3 border border-slate-100"
      style={{ background: colors.bg }}
      data-testid="kpi-transformation-hero"
    >
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={16} weight="fill" color={colors.accent} />
        <div className="overline" style={{ color: colors.fg }}>
          KPI principal · Transformation compromis → acte
        </div>
        <KpiInfoToggle
          expanded={expanded}
          onToggle={onToggle}
          testid="kpi-transformation-info"
        />
      </div>

      <div className="flex items-baseline gap-3">
        <div
          className="font-display text-5xl md:text-6xl font-extrabold tabular-nums leading-none"
          style={{ color: colors.fg }}
          data-testid="kpi-transformation-rate"
        >
          {hasData ? `${rate.toFixed(1).replace(".", ",")}` : "—"}
          <span className="text-2xl ml-1">%</span>
        </div>
      </div>

      {hasData && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px] font-bold">
          <span className="text-slate-700">
            Compromis : <span className="tabular-nums">{data.compromis_count}</span>
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-700">
            Actes : <span className="tabular-nums">{data.actes_count}</span>
          </span>
          {delta !== null && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">
                Marché : <span className="tabular-nums">{data.benchmark_pct}%</span>
              </span>
              <span
                className="px-2 py-0.5 rounded text-[11px] font-extrabold tabular-nums"
                style={{ background: colors.bg, color: colors.fg, border: `1px solid ${colors.accent}33` }}
              >
                {delta > 0 ? "+" : ""}
                {delta.toFixed(1).replace(".", ",")} pts
              </span>
            </>
          )}
        </div>
      )}

      {expanded && (
        <div
          className="mt-3 px-3 py-2.5 rounded-lg bg-white border border-slate-200 text-[11.5px] leading-relaxed text-slate-600"
          data-testid="kpi-transformation-definition"
        >
          <div className="mb-1">{data.definition}</div>
          <div className="text-slate-400 italic">
            Calculé sur {data.sample_size} dossier{data.sample_size > 1 ? "s" : ""} ·{" "}
            {periodLabel}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// KPI secondaire — carte compacte (KPI 2/3/4)
// ============================================================================
function KpiSecondary({ icon: Icon, label, value, suffix, hint, tone, sampleSize, definition, periodLabel, expanded, onToggle, testid }) {
  const colors = toneColors(tone);
  const hasData = value !== null && value !== undefined;

  return (
    <div className="card-base p-4" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} weight="fill" color={colors.accent} />
        <div className="text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500 flex-1 truncate">
          {label}
        </div>
        <KpiInfoToggle expanded={expanded} onToggle={onToggle} testid={`${testid}-info`} />
      </div>
      <div
        className="font-display text-2xl md:text-3xl font-extrabold tabular-nums"
        style={{ color: hasData ? colors.fg : "var(--cf-text-3)" }}
      >
        {hasData ? value : "—"}
        {hasData && suffix && (
          <span className="text-base font-bold ml-1" style={{ color: colors.fg, opacity: 0.7 }}>
            {suffix}
          </span>
        )}
      </div>
      {hint && (
        <div className="text-[11px] text-slate-500 font-semibold mt-1 leading-snug">{hint}</div>
      )}
      <div className="mt-2">
        <SampleBadge size={sampleSize} />
      </div>
      {expanded && (
        <div
          className="mt-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-[11px] leading-relaxed text-slate-600"
          data-testid={`${testid}-definition`}
        >
          <div className="mb-1">{definition}</div>
          <div className="text-slate-400 italic">{periodLabel}</div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Composant principal
// ============================================================================
export default function PerformanceCommerciale() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);

  useEffect(() => {
    api
      .get("/pilotage/performance-commerciale?period_months=12")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const periodLabel = data.period_label || "12 derniers mois";
  const t = data.taux_transformation;
  const r = data.respect_delais_offre;
  const a = data.avance_offre;
  const dco = data.delai_compromis_offre;
  const synthese = data.synthese || [];

  // Si aucun KPI ne dispose de donnée → Safe = Silence
  const hasAnyData =
    (t && t.rate_pct !== null) ||
    (r && r.rate_pct !== null) ||
    (a && a.avance_days !== null) ||
    (dco && dco.avg_days !== null);
  if (!hasAnyData) return null;

  const toggle = (key) => setExpanded(expanded === key ? null : key);

  // Helpers d'affichage des KPI 2/3/4
  const respectValue = r.rate_pct !== null ? `${Math.round(r.rate_pct)}` : null;
  const respectHint =
    r.rate_pct !== null
      ? `${r.dans_delais}/${r.sample_size} offres dans les délais annoncés`
      : "Aucune offre éditée avec date annoncée";

  const avanceValue =
    a.avance_days !== null ? `${Math.abs(a.avance_days).toFixed(0)}j` : null;
  const avanceSuffix =
    a.avance_days !== null
      ? a.avance_days > 0
        ? "d'avance"
        : a.avance_days < 0
        ? "de retard"
        : ""
      : null;
  const avanceHint =
    a.avg_prev_days !== null && a.avg_real_days !== null
      ? `Prévu : ${Math.round(a.avg_prev_days)}j · Réel : ${Math.round(a.avg_real_days)}j`
      : "Pas encore de mesure";

  const dcoValue = dco.avg_days !== null ? `${Math.round(dco.avg_days)}j` : null;
  const dcoHint =
    dco.avg_days !== null
      ? `Marché : ${dco.benchmark_days}j${
          dco.gain_days !== null
            ? dco.gain_days > 0
              ? ` · Gain ${Math.round(dco.gain_days)}j`
              : dco.gain_days < 0
              ? ` · Retard ${Math.abs(Math.round(dco.gain_days))}j`
              : " · Au niveau"
            : ""
        }`
      : "Aucune donnée disponible";

  return (
    <section className="card-base p-5 mb-4" data-testid="performance-commerciale-card">
      <div className="flex items-center gap-2 mb-1">
        <Briefcase size={18} weight="fill" color="var(--cf-accent)" />
        <h2 className="font-display text-base md:text-lg font-extrabold text-slate-900">
          Performance commerciale
        </h2>
      </div>
      <p className="text-[12px] text-slate-500 font-semibold mb-4 leading-snug">
        Pourquoi un agent immobilier devrait-il te confier ses acquéreurs ?
      </p>

      {/* KPI 1 HERO */}
      <KpiTransformationHero
        data={t}
        periodLabel={periodLabel}
        expanded={expanded === "transformation"}
        onToggle={() => toggle("transformation")}
      />

      {/* KPIs 2/3/4 en grille */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <KpiSecondary
          icon={CheckCircle}
          label="Respect délais offre"
          value={respectValue}
          suffix="%"
          hint={respectHint}
          tone={
            r.rate_pct === null
              ? "muted"
              : r.rate_pct >= 85
              ? "good"
              : r.rate_pct >= 70
              ? "warning"
              : "bad"
          }
          sampleSize={r.sample_size || null}
          definition={r.definition}
          periodLabel={periodLabel}
          expanded={expanded === "respect"}
          onToggle={() => toggle("respect")}
          testid="kpi-respect-delais"
        />
        <KpiSecondary
          icon={Lightning}
          label="Avance sur l'offre"
          value={avanceValue}
          suffix={avanceSuffix}
          hint={avanceHint}
          tone={a.tone}
          sampleSize={a.sample_size || null}
          definition={a.definition}
          periodLabel={periodLabel}
          expanded={expanded === "avance"}
          onToggle={() => toggle("avance")}
          testid="kpi-avance-offre"
        />
        <KpiSecondary
          icon={Clock}
          label="Compromis → offre"
          value={dcoValue}
          suffix=""
          hint={dcoHint}
          tone={dco.tone}
          sampleSize={dco.sample_size || null}
          definition={dco.definition}
          periodLabel={periodLabel}
          expanded={expanded === "dco"}
          onToggle={() => toggle("dco")}
          testid="kpi-delai-compromis-offre"
        />
      </div>

      {/* Carte de synthèse commerciale */}
      {synthese.length > 0 && (
        <div
          className="rounded-2xl p-5 bg-gradient-to-br from-[color:var(--cf-success-soft)] to-[color:var(--cf-success-soft)] border border-[color:var(--cf-success-border)]"
          data-testid="synthese-commerciale"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={16} weight="fill" color="var(--cf-success-text)" />
            <div className="overline" style={{ color: "var(--cf-success-text)" }}>
              Pourquoi travailler avec moi ?
            </div>
          </div>
          <ul className="space-y-2">
            {synthese.map((s, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-[13.5px] font-bold text-[color:var(--cf-success-text)] leading-snug"
                data-testid={`synthese-bullet-${idx}`}
              >
                <CheckCircle size={16} weight="fill" color="var(--cf-success)" className="flex-shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-[color:var(--cf-success-border)] text-[10.5px] font-bold text-[color:var(--cf-success-text)] tabular-nums">
            Données sur {periodLabel.toLowerCase()}
          </div>
        </div>
      )}

      {/* Méthodologie globale */}
      <button
        type="button"
        onClick={() => setShowMethodology(!showMethodology)}
        className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-slate-700 transition-colors"
        data-testid="perf-com-methodology-toggle"
      >
        <CaretDown
          size={11}
          weight="bold"
          className={`transition-transform ${showMethodology ? "rotate-180" : ""}`}
        />
        Méthodologie & transparence
      </button>
      {showMethodology && (
        <div
          className="mt-2 px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-[11.5px] leading-relaxed text-slate-600 space-y-1.5"
          data-testid="perf-com-methodology-content"
        >
          <p>
            <strong>Période</strong> : {periodLabel} (depuis le {data.period_start || "—"}).
          </p>
          <p>
            <strong>KPI 1 — Transformation compromis → acte</strong> : actes signés /
            compromis signés sur la période. Inclut les dossiers en cours non encore signés
            (numérateur ≤ dénominateur).
          </p>
          <p>
            <strong>KPI 2 — Respect des délais offre</strong> : part des offres éditées à la
            date prévisionnelle annoncée (ou avant).
          </p>
          <p>
            <strong>KPI 3 — Avance sur l'offre</strong> : moyenne(prévisionnel − réel) en jours
            calendaires. Positif = avance, négatif = retard.
          </p>
          <p>
            <strong>KPI 4 — Compromis → offre</strong> : moyenne des écarts entre date de
            compromis et date d'offre réelle. Comparé au benchmark marché paramétrable.
          </p>
          <p className="text-slate-400 italic">
            Pour un usage commercial externe (RDV agent, support, réseau), échantillon
            recommandé : ≥ {data.min_reliable_sample || MIN_SAMPLE} dossiers par KPI.
          </p>
        </div>
      )}
    </section>
  );
}
